import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RocketLaunch as RocketIcon } from '@mui/icons-material';
import { DeploymentsPanel } from '../Deployments/DeploymentsPanel';
import { SheetsPanel } from '../Sheets/SheetsPanel';
import { TableChart as SheetIcon } from '@mui/icons-material';
import {
  Add as AddIcon,
  MoreHoriz as MoreHorizIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  FilterList as FilterListIcon,
  PersonAdd as PersonAddIcon,
  Chat as ChatIcon,
  Assignment as TasksIcon,
  CalendarToday as CalendarIcon,
  Note as NotesIcon,
  People as MembersIcon,
  Settings as SettingsIcon,
  BugReport as BugIcon,
  AutoStories as StoryIcon,
  CheckBox as TaskIcon,
  Bolt as EpicIcon,
  Description as DefaultTypeIcon
} from '@mui/icons-material';
import { BarChart3, Search, X, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { Project, Issue, User, ChatMessage } from '../../types';
import { IssueMessenger } from '../Messenger/IssueMessenger';
import { apiService } from '../../services/api';
import { ProjectCalendar } from './ProjectCalendar';
import { ProjectNotes } from './ProjectNotes';
import { ProjectInvitations } from './ProjectInvitations';
import { ProjectDashboard } from './ProjectDashboard';
import { UserAvatar } from '../UI/UserAvatar';
import { toast } from '../UI/Toast';

interface ProjectKanbanProps {
  project: Project;
  issues: Issue[];
  users: User[];
  currentUser: User;
  onIssueClick: (issue: Issue) => void;
  onCreateIssue: (projectId: string) => void;
  onEditProject: (project: Project) => void;
  onDeleteProject: (projectId: string) => void;
  onUpdateIssue: (issueId: string, updates: Partial<Issue>) => void;
  onDeleteIssue: (issueId: string) => void;
  onBackToProjects: () => void;
  onRefreshProjectIssues?: (projectId: string) => Promise<void>;
  initialTab?: string;
  onTabChange?: (tab: string) => void;
}

export const ProjectKanban: React.FC<ProjectKanbanProps> = ({
  project,
  issues,
  users,
  currentUser,
  onIssueClick,
  onCreateIssue,
  onEditProject,
  onDeleteProject,
  onUpdateIssue,
  onDeleteIssue,
  onBackToProjects,
  onRefreshProjectIssues,
  initialTab,
  onTabChange,
}) => {
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [draggedIssue, setDraggedIssue] = useState<Issue | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<string | null>(null);
  const [messengerIssue, setMessengerIssue] = useState<Issue | null>(null);
  const [isMessengerOpen, setIsMessengerOpen] = useState(false);
  const [messengerMessages, setMessengerMessages] = useState<ChatMessage[]>([]);
  const [activeToolsTab, setActiveToolsTabState] = useState(initialTab || 'board');

  const setActiveToolsTab = (tab: string) => {
    setActiveToolsTabState(tab);
    onTabChange?.(tab);
  };
  const [showToolsSection, setShowToolsSection] = useState(initialTab && initialTab !== 'board' ? true : false);
  const [isLoadingIssues, setIsLoadingIssues] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const [filters, setFilters] = useState({
    assignee: 'all',
    type: 'all',
    priority: 'all',
    reporter: 'all',
    labels: 'all',
    dueDateFrom: '',
    dueDateTo: ''
  });

  const [projectMembers, setProjectMembers] = useState<User[]>([]);
  const [projectMembersLoading, setProjectMembersLoading] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  // Per-column visible issue count for lazy loading (show 10 initially, load more on scroll)
  const ISSUES_PER_PAGE = 10;
  const [columnVisibleCounts, setColumnVisibleCounts] = useState<Record<string, number>>({});

  const getVisibleCount = (columnId: string) => columnVisibleCounts[columnId] || ISSUES_PER_PAGE;

  const loadMoreForColumn = (columnId: string) => {
    setColumnVisibleCounts(prev => ({
      ...prev,
      [columnId]: (prev[columnId] || ISSUES_PER_PAGE) + ISSUES_PER_PAGE
    }));
  };

  // Reset visible counts when filters change
  useEffect(() => {
    setColumnVisibleCounts({});
  }, [filters, searchQuery]);

  const handleAssigneeFilterChange = (value: string) => {
    setFilters((prev) => ({ ...prev, assignee: value }));
  };

  const isAssigneeFilterActive = (value: string) => filters.assignee === value;

  // Filter issues based on current filters
  const getFilteredIssues = () => {
    // Ensure issues is an array before filtering
    if (!Array.isArray(issues)) {
      return [];
    }

    let filteredIssues = issues;

    // Search filter
    if (searchQuery) {
      filteredIssues = filteredIssues.filter(issue =>
        issue.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
        issue.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
        issue.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Assignee filter
    if (filters.assignee !== 'all') {
      if (filters.assignee === 'unassigned') {
        filteredIssues = filteredIssues.filter(issue => !issue.assignee);
      } else {
        filteredIssues = filteredIssues.filter(issue => issue.assignee === filters.assignee);
      }
    }

    // Reporter filter
    if (filters.reporter !== 'all') {
      if (filters.reporter === 'unknown') {
        filteredIssues = filteredIssues.filter(issue => !issue.reporter);
      } else {
        filteredIssues = filteredIssues.filter(issue => issue.reporter === filters.reporter);
      }
    }

    // Type filter
    if (filters.type !== 'all') {
      filteredIssues = filteredIssues.filter(issue => issue.type === filters.type);
    }

    // Priority filter
    if (filters.priority !== 'all') {
      filteredIssues = filteredIssues.filter(issue => issue.priority === filters.priority);
    }

    // Due date range filter
    if (filters.dueDateFrom || filters.dueDateTo) {
      filteredIssues = filteredIssues.filter(issue => {
        if (!issue.dueDate) return false; // Exclude issues without due dates

        const issueDate = new Date(issue.dueDate);
        const fromDate = filters.dueDateFrom ? new Date(filters.dueDateFrom) : null;
        const toDate = filters.dueDateTo ? new Date(filters.dueDateTo) : null;

        if (fromDate && issueDate < fromDate) return false;
        if (toDate && issueDate > toDate) return false;

        return true;
      });
    }

    return filteredIssues;
  };

  // Get filtered issues
  const filteredIssues = getFilteredIssues();

  const activeFilterCount = [filters.assignee, filters.type, filters.priority, filters.reporter].filter(f => f !== 'all').length
    + (filters.dueDateFrom || filters.dueDateTo ? 1 : 0);

  const columns = [
    {
      id: 'to-do',
      title: 'To Do',
      statuses: ['to-do', 'backlog'],
      dotColor: 'bg-surface-400',
      headerBg: 'bg-surface-50',
      count: Array.isArray(filteredIssues) ? filteredIssues.filter(i => ['to-do', 'backlog'].includes(i.status)).length : 0
    },
    {
      id: 'in-progress',
      title: 'In Progress',
      statuses: ['in-progress'],
      dotColor: 'bg-orange-400',
      headerBg: 'bg-orange-50/50',
      count: Array.isArray(filteredIssues) ? filteredIssues.filter(i => i.status === 'in-progress').length : 0
    },
    {
      id: 'in-review',
      title: 'In Review',
      statuses: ['in-review'],
      dotColor: 'bg-purple-400',
      headerBg: 'bg-purple-50/50',
      count: Array.isArray(filteredIssues) ? filteredIssues.filter(i => i.status === 'in-review').length : 0
    },
    {
      id: 'done',
      title: 'Done',
      statuses: ['done'],
      dotColor: 'bg-green-400',
      headerBg: 'bg-green-50/50',
      count: Array.isArray(filteredIssues) ? filteredIssues.filter(i => i.status === 'done').length : 0
    },
    {
      id: 'on-hold',
      title: 'On Hold',
      statuses: ['on-hold'] as any[],
      dotColor: 'bg-red-400',
      headerBg: 'bg-red-50/50',
      count: Array.isArray(filteredIssues) ? filteredIssues.filter(i => (i.status as string) === 'on-hold').length : 0
    }
  ];

  const toolsItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, active: false },
    { id: 'board', label: 'Board', icon: FilterListIcon, active: true },
    { id: 'tasks', label: 'Tasks', icon: TasksIcon },
    { id: 'calendar', label: 'Calendar', icon: CalendarIcon },
    { id: 'invitations', label: 'Invitations', icon: PersonAddIcon },
    { id: 'notes', label: 'Notes', icon: NotesIcon },
    { id: 'members', label: 'Members', icon: MembersIcon },
    { id: 'sheets', label: 'Sheets', icon: SheetIcon },
    { id: 'deployments', label: 'Deployments', icon: RocketIcon },
    { id: 'settings', label: 'Settings', icon: SettingsIcon }
  ];

  // Load project issues when component mounts or project changes
  useEffect(() => {
    loadProjectIssues();
    // `onRefreshProjectIssues` is intentionally omitted to avoid
    // triggering duplicate fetches when its reference changes
  }, [project.id]);

  // Subscribe to project updates via websocket
  useEffect(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    let ws: WebSocket | null = null;
    try {
      ws = new WebSocket(`${protocol}://${window.location.host}/api/ws/projects/${project.id}`);
      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          // Only refresh if the update was NOT triggered by the current user's
          // drag-and-drop (i.e. we are not in the middle of a status update).
          // This prevents a race condition where the WS refresh overwrites the
          // optimistic update before the API call completes.
          if (data.type === 'issue_updated' && onRefreshProjectIssues && !isUpdatingStatus) {
            await onRefreshProjectIssues(project.id);
          }
        } catch (err) {
          console.error('Failed to handle websocket message', err);
        }
      };
      ws.onerror = () => {
        // Silently ignore WebSocket connection errors (e.g. when WS endpoint is unavailable)
        console.warn('WebSocket connection error for project:', project.id);
      };
    } catch (err) {
      console.warn('Failed to create WebSocket connection:', err);
    }
    return () => {
      if (ws) {
        try { ws.close(); } catch { /* ignore */ }
      }
    };
  }, [project.id]);

  const loadProjectIssues = async () => {
    try {
      setIsLoadingIssues(true);
      setApiError(null);

      console.log(`Loading issues for project: ${project.name} (${project.id})`);

      // Check if refresh function is available, otherwise use existing data
      if (onRefreshProjectIssues) {
        await onRefreshProjectIssues(project.id);
        console.log('Successfully refreshed project issues from API');
      } else {
        console.log('Using existing project issues data');
      }

    } catch (error) {
      console.error('Failed to load project issues:', error);
      setApiError('Failed to refresh issues from API. Showing cached data.');
    } finally {
      setIsLoadingIssues(false);
    }
  };


  const getIssuesByStatus = (statuses: string[]) => {
    return Array.isArray(filteredIssues) ? filteredIssues.filter(issue => statuses.includes(issue.status)) : [];
  };

  const handleDragStart = (e: React.DragEvent, issue: Issue) => {
    if (isLoadingIssues || isUpdatingStatus) {
      e.preventDefault();
      return;
    }

    setDraggedIssue(issue);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', issue.id);

    // Add visual feedback to the dragged element
    const target = e.target as HTMLElement;
    target.style.opacity = '0.5';
    target.style.transform = 'rotate(5deg)';

    // Show success toast for drag start
    toast.info(`Dragging ${issue.key}`, {
      description: 'Drop in a column to change status',
      duration: 2000
    });
  };

  const handleDragEnd = (e: React.DragEvent) => {
    // Reset visual feedback
    const target = e.target as HTMLElement;
    target.style.opacity = '1';
    target.style.transform = 'none';

    // Clear drag state
    setDraggedIssue(null);
    setDragOverColumn(null);
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    if (draggedIssue && !isUpdatingStatus) {
      setDragOverColumn(columnId);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the column entirely
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverColumn(null);
    }
  };
  const handleDrop = async (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (draggedIssue && draggedIssue.status !== targetStatus) {
      const issueToMove = draggedIssue;
      // Clear dragged issue immediately so UI is responsive
      setDraggedIssue(null);

      try {
        setIsUpdatingStatus(issueToMove.id);

        // Show loading toast
        toast.loading(`Moving ${issueToMove.key} to ${targetStatus.replace('-', ' ').toUpperCase()}...`, {
          id: `update-${issueToMove.id}`
        });

        // Call parent handler which does optimistic update + API call
        await onUpdateIssue(issueToMove.id, { status: targetStatus as any });

        // Show success toast
        toast.success(`${issueToMove.key} moved to ${targetStatus.replace('-', ' ').toUpperCase()}`, {
          id: `update-${issueToMove.id}`,
          description: 'Status updated successfully'
        });

      } catch (error) {
        console.error('Failed to update issue status:', error);
        toast.error(`Failed to move ${issueToMove.key}`, {
          id: `update-${issueToMove.id}`,
          description: 'Please try again or refresh the page'
        });
      } finally {
        setIsUpdatingStatus(null);
      }
    } else if (draggedIssue && draggedIssue.status === targetStatus) {
      // Show info toast if dropped in same column
      toast.info(`${draggedIssue.key} is already in ${targetStatus.replace('-', ' ').toUpperCase()}`, {
        duration: 2000
      });
      setDraggedIssue(null);
    } else {
      setDraggedIssue(null);
    }
  };

  const handleDeleteProject = () => {
    if (window.confirm(`Are you sure you want to delete "${project.name}"? This action cannot be undone.`)) {
      onDeleteProject(project.id);
    }
  };

  const handleDeleteIssue = async (issue: Issue) => {
    if (!window.confirm(`Are you sure you want to delete "${issue.summary}"? This action cannot be undone.`)) return;
    try {
      await onDeleteIssue(issue.id);
      toast.success('Issue deleted successfully');
    } catch (error) {
      console.error('Failed to delete issue:', error);
      toast.error('Failed to delete issue');
    }
  };

  const handleOpenMessenger = (issue: Issue) => {
    openMessenger(issue);
  };

  const handleIssueCardClick = async (issue: Issue) => {
    try {
      // Load full issue details when card is clicked
      console.log('Loading full issue details for:', issue.key);
      const fullIssueDetails = await apiService.getIssue(issue.id);

      // Convert API response to frontend format
      const detailedIssue = {
        ...issue,
        description: fullIssueDetails.description || '',
        labels: fullIssueDetails.labels || [],
        components: fullIssueDetails.components || [],
        fixVersions: fullIssueDetails.fix_versions || [],
        subtasks: [],
        attachments: [],
        comments: [],
        worklog: [],
        assignmentHistory: []
      };

      onIssueClick(detailedIssue);
    } catch (error) {
      console.error('Failed to load issue details:', error);
      // Fallback to basic issue data
      onIssueClick(issue);
    }
  };
  const openMessenger = async (issue: Issue) => {
    setMessengerIssue(issue);
    setIsMessengerOpen(true);
    try {
      const msgs = await apiService.getChatMessages(issue.id);
      setMessengerMessages(msgs);
    } catch (err) {
      console.error('Failed to load messages', err);
    }
  };

  const handleSendMessage = async (content: string, file?: File) => {
    if (!messengerIssue) return;
    try {
      let fileUrl: string | undefined;
      let fileName: string | undefined;
      let fileSize: number | undefined;
      let messageType: string | undefined;

      if (file) {
        const uploadRes = await apiService.uploadChatFile(messengerIssue.id, file);
        fileUrl = uploadRes.file_url;
        fileName = uploadRes.file_name;
        fileSize = uploadRes.file_size;
        messageType = uploadRes.message_type;
      }

      const msg = await apiService.sendChatMessage(messengerIssue.id, {
        content,
        fileUrl,
        fileName,
        fileSize,
        messageType,
      });
      setMessengerMessages(prev => [...prev, msg]);
    } catch (err) {
      console.error('Failed to send message', err);
      toast.error('Failed to send message');
    }
  };

  const projectLead = users.find(u => u.id === project.lead);
  const isProjectLead = project.lead === currentUser.id;

  const loadProjectMembers = useCallback(async () => {
    try {
      setProjectMembersLoading(true);
      const members = await apiService.getProjectMembers(project.id);
      setProjectMembers(members);
    } catch (error) {
      console.error('Failed to load project members', error);
      toast.error('Failed to load project members');
    } finally {
      setProjectMembersLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    loadProjectMembers();
  }, [loadProjectMembers]);

  const filteredProjectMembers = useMemo(() => {
    const query = memberSearch.trim().toLowerCase();
    if (!query) return projectMembers;
    return projectMembers.filter(member =>
      member.displayName.toLowerCase().includes(query) ||
      member.emailAddress.toLowerCase().includes(query)
    );
  }, [memberSearch, projectMembers]);

  const handleInviteMember = useCallback(async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setInviteLoading(true);
      const result = await apiService.addProjectMember(project.id, inviteEmail.trim());
      if (result.status === 'added' && result.member) {
        toast.success('Member added to project');
        setInviteEmail('');
        setShowAddMemberModal(false);
        await loadProjectMembers();
      } else if (result.status === 'invited') {
        toast.success('Invitation sent to the user');
        setInviteEmail('');
        setShowAddMemberModal(false);
      } else {
        toast.info(result.message || 'Request completed');
        await loadProjectMembers();
      }
    } catch (error) {
      console.error('Failed to add member', error);
      toast.error('Failed to add project member');
    } finally {
      setInviteLoading(false);
    }
  }, [inviteEmail, loadProjectMembers, project.id]);

  const handleRemoveMember = useCallback(async (memberId: string) => {
    try {
      await apiService.removeProjectMember(project.id, memberId);
      toast.success('Member removed from project');
      await loadProjectMembers();
    } catch (error) {
      console.error('Failed to remove member', error);
      toast.error('Failed to remove member');
    }
  }, [loadProjectMembers, project.id]);

  const renderUserAvatar = (user: User) => {
    return (
      <UserAvatar
        name={user.displayName}
        src={user.avatarUrl}
        className="w-8 h-8 border border-gray-200"
      />
    );
  };

  const renderToolsContent = () => {
    switch (activeToolsTab) {
      case 'sheets':
        return (
          <div className="p-6 bg-gray-50 min-h-[600px]">
            <SheetsPanel projectId={project.id} />
          </div>
        );
      case 'deployments':
        return (
          <div className="p-6 bg-gray-50 min-h-[600px]">
            <DeploymentsPanel projectId={project.id} showProjectColumn={false} />
          </div>
        );
      case 'dashboard':
        return (
          <ProjectDashboard
            project={project}
            issues={issues}
            users={users}
            currentUser={currentUser}
            onIssueClick={onIssueClick}
            onCreateIssue={() => onCreateIssue(project.id)}
          />
        );
      case 'members':
        return (
          <div className="p-6 space-y-5 bg-white min-h-[600px]">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Project Members</h3>
                <p className="text-sm text-gray-500 mt-1">{filteredProjectMembers.length} member{filteredProjectMembers.length !== 1 ? 's' : ''} in this project</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 md:items-center">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search members..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    className="pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400 w-full sm:w-64"
                  />
                </div>
                {isProjectLead && (
                  <button
                    onClick={() => setShowAddMemberModal(true)}
                    className="flex items-center space-x-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors text-sm font-medium"
                  >
                    <PersonAddIcon style={{ fontSize: 16 }} />
                    <span>Add People</span>
                  </button>
                )}
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="text-2xl font-bold text-gray-900">{filteredProjectMembers.length}</div>
                <div className="text-xs text-gray-500 mt-1">Total Members</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="text-2xl font-bold text-emerald-600">{filteredProjectMembers.filter(u => u.active).length}</div>
                <div className="text-xs text-gray-500 mt-1">Active</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="text-2xl font-bold text-amber-500">1</div>
                <div className="text-xs text-gray-500 mt-1">Project Lead</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="text-2xl font-bold text-blue-600">{filteredProjectMembers.filter(u => u.id !== project.lead).length}</div>
                <div className="text-xs text-gray-500 mt-1">Contributors</div>
              </div>
            </div>

            {/* Members list */}
            {projectMembersLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="ml-3 text-gray-500">Loading members…</span>
              </div>
            ) : filteredProjectMembers.length > 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr className="text-left text-xs font-medium text-gray-500">
                        <th className="px-4 py-2.5">Member</th>
                        <th className="px-4 py-2.5">Role</th>
                        <th className="px-4 py-2.5">Status</th>
                        <th className="px-4 py-2.5 text-right whitespace-nowrap">Assigned</th>
                        <th className="px-4 py-2.5 text-right whitespace-nowrap">Done</th>
                        <th className="px-4 py-2.5 w-44">Progress</th>
                        {isProjectLead && <th className="px-4 py-2.5 w-24"></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredProjectMembers.map((user) => {
                        const isLead = user.id === project.lead;
                        const userIssues = issues.filter(i => {
                          const assignees = i.assignees || [];
                          return assignees.some((a: any) => (typeof a === 'string' ? a : a?.id) === user.id);
                        });
                        const completedIssues = userIssues.filter(i => i.status === 'done').length;
                        const pct = userIssues.length > 0 ? (completedIssues / userIssues.length) * 100 : 0;

                        return (
                          <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3 min-w-0">
                                {renderUserAvatar(user)}
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-900 text-sm flex items-center gap-2">
                                    <span className="truncate">{user.displayName}</span>
                                    {isLead && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                        Lead
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate">{user.emailAddress}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                              {isLead ? 'Project Owner' : 'Project Member'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${user.active
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'bg-gray-100 text-gray-500 border-gray-200'
                                }`}>
                                <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${user.active ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                                {user.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-700">{userIssues.length}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-700">{completedIssues}</td>
                            <td className="px-4 py-3">
                              {userIssues.length > 0 ? (
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-500"
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                  <span className="text-xs text-gray-400 w-9 text-right tabular-nums">{Math.round(pct)}%</span>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </td>
                            {isProjectLead && (
                              <td className="px-4 py-3 text-right">
                                {!isLead && (
                                  <button
                                    onClick={() => handleRemoveMember(user.id)}
                                    className="text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
                                  >
                                    Remove
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center bg-white border border-gray-200 rounded-xl">
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
                  <MembersIcon style={{ fontSize: 32, color: '#9ca3af' }} />
                </div>
                <h4 className="text-lg font-semibold text-gray-700 mb-2">No members found</h4>
                <p className="text-sm text-gray-500">Try adjusting your search or add new members to this project.</p>
              </div>
            )}
          </div>
        );
      case 'tasks':
        return (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Project Tasks</h3>
              <button
                onClick={() => onCreateIssue(project.id)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
              >
                <AddIcon style={{ fontSize: 16 }} />
                <span>Create task</span>
              </button>
            </div>

            <div className="space-y-3">
              {issues.map((issue) => (
                <div key={issue.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-medium text-blue-600">{issue.key}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${issue.status === 'done' ? 'bg-green-100 text-green-800' :
                          issue.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                          {issue.status.replace('-', ' ').toUpperCase()}
                        </span>
                      </div>
                      <h4 className="font-medium text-gray-900 mb-1">{issue.summary}</h4>
                      <p className="text-sm text-gray-600 line-clamp-2">{issue.description}</p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <div className="flex -space-x-1">
                        {(() => {
                          const assigneeIds = issue.assignees?.length
                            ? issue.assignees
                            : issue.assignee
                              ? [issue.assignee]
                              : [];
                          return assigneeIds.slice(0, 3).map((assigneeId) => {
                            const assigneeUser = users.find(u => u.id === assigneeId);
                            return assigneeUser ? (
                              <UserAvatar
                                key={assigneeUser.id}
                                name={assigneeUser.displayName}
                                src={assigneeUser.avatarUrl}
                                className="w-6 h-6 border border-white shadow-sm"
                              />
                            ) : null;
                          });
                        })()}
                        {issue.assignees && issue.assignees.length > 3 && (
                          <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600">
                            +{issue.assignees.length - 3}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'calendar':
        return (
          <ProjectCalendar
            project={project}
            issues={issues}
            users={users}
            onCreateIssue={onCreateIssue}
            onIssueClick={onIssueClick}
          />
        );
      case 'invitations':
        return (
          <ProjectInvitations
            project={project}
            currentUser={currentUser}
          />
        );
      case 'notes':
        return (
          <ProjectNotes
            project={project}
            currentUser={currentUser}
          />
        );
      case 'settings':
        return (
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Project Settings</h3>
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <SettingsIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Settings panel coming soon...</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="h-full flex bg-surface-900">
        {/* Tools Sidebar */}
        <div className={`bg-surface-800 border-r border-surface-700 transition-all duration-300 ${showToolsSection ? 'w-64' : 'w-16'
          }`}>
          <div className="p-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className={`font-semibold text-white transition-opacity duration-300 ${showToolsSection ? 'opacity-100' : 'opacity-0'
                }`}>
                Tools
              </h2>
              <button
                onClick={() => setShowToolsSection(!showToolsSection)}
                className="p-1 text-surface-400 hover:text-white rounded"
              >
                <FilterListIcon style={{ fontSize: 20 }} />
              </button>
            </div>

            <nav className="space-y-1">
              {toolsItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeToolsTab === item.id;

                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveToolsTab(item.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive
                      ? 'bg-brand-600/20 text-brand-300'
                      : 'text-surface-400 hover:bg-surface-700 hover:text-white'
                      }`}
                    title={!showToolsSection ? item.label : undefined}
                  >
                    <Icon style={{ fontSize: 18 }} />
                    <span className={`transition-opacity duration-300 ${showToolsSection ? 'opacity-100' : 'opacity-0'
                      }`}>
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Project Header */}
            {/* Project Header - one compact row: identity, search, filters, team, menu */}
            <div className="bg-surface-800 border-b border-surface-700 px-6 py-2.5">
              <div className="flex items-center gap-2.5 flex-wrap">
                <button
                  onClick={onBackToProjects}
                  className="text-brand-400 hover:text-brand-300 text-sm font-medium transition-colors shrink-0"
                >
                  Projects
                </button>
                <span className="text-surface-600 shrink-0">/</span>

                <div className="w-7 h-7 bg-brand-600 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-sm shrink-0">
                  {project.avatar}
                </div>
                <h1 className="text-base font-semibold text-white truncate max-w-[240px]" title={project.name}>
                  {project.name}
                </h1>
                <span className="text-2xs text-surface-400 bg-surface-700 px-1.5 py-0.5 rounded shrink-0 capitalize">
                  {project.type.replace('-', ' ')}
                </span>
                <span className="text-2xs text-surface-400 bg-surface-700 px-1.5 py-0.5 rounded font-mono shrink-0">
                  {project.key}
                </span>

                {activeToolsTab === 'board' && (
                  <>
                    {/* Search */}
                    <div className="relative flex-1 min-w-[160px] max-w-xs">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-surface-400" />
                      <input
                        type="text"
                        placeholder="Search issues..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-7 py-1.5 bg-surface-700 border border-surface-600 rounded-lg text-sm text-surface-200 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all placeholder:text-surface-400"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-200"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {/* Filter toggle */}
                    <button
                      onClick={() => setShowFiltersPanel(!showFiltersPanel)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium border transition-all shrink-0 ${showFiltersPanel || activeFilterCount > 0
                        ? 'bg-brand-600/20 border-brand-500/40 text-brand-300'
                        : 'bg-surface-700 border-surface-600 text-surface-300 hover:bg-surface-600'
                        }`}
                    >
                      <SlidersHorizontal className="w-3.5 h-3.5" />
                      Filters
                      {activeFilterCount > 0 && (
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-2xs font-bold">
                          {activeFilterCount}
                        </span>
                      )}
                    </button>

                    {/* Clear */}
                    {activeFilterCount > 0 && (
                      <button
                        onClick={() => {
                          setFilters({ assignee: 'all', type: 'all', priority: 'all', reporter: 'all', labels: 'all', dueDateFrom: '', dueDateTo: '' });
                          setSearchQuery('');
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all shrink-0"
                      >
                        <X className="w-3.5 h-3.5" />
                        Clear
                      </button>
                    )}

                    {/* Issue count */}
                    <span className="text-xs text-surface-400 shrink-0 whitespace-nowrap">
                      {filteredIssues.length} of {issues.length}
                    </span>

                    {/* Team */}
                    <div className="flex items-center -space-x-2 shrink-0">
                      {users.slice(0, 4).map((user) => (
                        <UserAvatar
                          key={user.id}
                          name={user.displayName}
                          src={user.avatarUrl}
                          className="w-6 h-6 border-2 border-surface-800 shadow-sm"
                        />
                      ))}
                      {isProjectLead && (
                        <button
                          onClick={() => setShowAddMemberModal(true)}
                          title="Add people"
                          className="w-6 h-6 bg-surface-600 rounded-full border-2 border-surface-800 flex items-center justify-center text-surface-300 hover:bg-surface-500 transition-colors"
                        >
                          <PersonAddIcon style={{ fontSize: 12 }} />
                        </button>
                      )}
                    </div>

                    {/* Refresh */}
                    <button
                      onClick={loadProjectIssues}
                      disabled={isLoadingIssues}
                      title="Refresh issues"
                      className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-brand-400 hover:bg-brand-500/10 border border-brand-500/30 transition-all disabled:opacity-50 shrink-0"
                    >
                      {isLoadingIssues ? (
                        <div className="w-3.5 h-3.5 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <span className="text-sm leading-none">↻</span>
                      )}
                    </button>
                  </>
                )}

                {/* Project menu */}
                <div className="relative shrink-0 ml-auto">
                  <button
                    onClick={() => setShowProjectMenu(!showProjectMenu)}
                    className="p-1.5 text-surface-400 hover:text-white hover:bg-surface-700 rounded-lg transition-colors flex items-center"
                  >
                    <MoreHorizIcon style={{ fontSize: 18 }} />
                  </button>

                  {showProjectMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-surface-700 rounded-lg shadow-lg border border-surface-600 py-2 z-50">
                      <button
                        onClick={() => {
                          onEditProject(project);
                          setShowProjectMenu(false);
                        }}
                        className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-surface-200 hover:bg-surface-600"
                      >
                        <EditIcon style={{ fontSize: 16 }} />
                        <span>Edit Project</span>
                      </button>
                      <button
                        onClick={() => {
                          handleDeleteProject();
                          setShowProjectMenu(false);
                        }}
                        className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-400 hover:bg-surface-600"
                      >
                        <DeleteIcon style={{ fontSize: 16 }} />
                        <span>Delete Project</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Expandable filter panel lives below the row; empty:hidden keeps
                  it from adding a gap while it is closed. */}
              {activeToolsTab === 'board' && (
                <div className="space-y-4 mt-3 empty:hidden empty:mt-0">

                {/* Expandable Filter Panel */}
                {showFiltersPanel && (
                  <div className="p-4 bg-surface-800 border border-surface-600 rounded-2xl shadow-sm">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      {/* Assignee */}
                      <div>
                        <label className="block text-xs font-semibold text-surface-400 uppercase tracking-wider mb-1.5">Assignee</label>
                        <div className="relative">
                          <select
                            value={filters.assignee}
                            onChange={(e) => handleAssigneeFilterChange(e.target.value)}
                            className="w-full appearance-none px-3 py-2.5 pr-9 bg-surface-700 border border-surface-600 rounded-xl text-sm text-surface-200 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all cursor-pointer"
                          >
                            <option value="all">All Assignees</option>
                            <option value="unassigned">Unassigned</option>
                            {projectMembers.map(user => (
                              <option key={user.id} value={user.id}>{user.displayName}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400 pointer-events-none" />
                        </div>
                      </div>

                      {/* Reporter */}
                      <div>
                        <label className="block text-xs font-semibold text-surface-400 uppercase tracking-wider mb-1.5">Reporter</label>
                        <div className="relative">
                          <select
                            value={filters.reporter}
                            onChange={(e) => setFilters({ ...filters, reporter: e.target.value })}
                            className="w-full appearance-none px-3 py-2.5 pr-9 bg-surface-700 border border-surface-600 rounded-xl text-sm text-surface-200 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all cursor-pointer"
                          >
                            <option value="all">All Reporters</option>
                            <option value="unknown">Unknown</option>
                            {projectMembers.map(user => (
                              <option key={user.id} value={user.id}>{user.displayName}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400 pointer-events-none" />
                        </div>
                      </div>

                      {/* Type */}
                      <div>
                        <label className="block text-xs font-semibold text-surface-400 uppercase tracking-wider mb-1.5">Type</label>
                        <div className="relative">
                          <select
                            value={filters.type}
                            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                            className="w-full appearance-none px-3 py-2.5 pr-9 bg-surface-700 border border-surface-600 rounded-xl text-sm text-surface-200 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all cursor-pointer"
                          >
                            <option value="all">All Types</option>
                            <option value="story">📖 Story</option>
                            <option value="task">✅ Task</option>
                            <option value="bug">🐛 Bug</option>
                            <option value="epic">🚀 Epic</option>
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400 pointer-events-none" />
                        </div>
                      </div>

                      {/* Priority */}
                      <div>
                        <label className="block text-xs font-semibold text-surface-400 uppercase tracking-wider mb-1.5">Priority</label>
                        <div className="relative">
                          <select
                            value={filters.priority}
                            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                            className="w-full appearance-none px-3 py-2.5 pr-9 bg-surface-700 border border-surface-600 rounded-xl text-sm text-surface-200 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all cursor-pointer"
                          >
                            <option value="all">All Priorities</option>
                            <option value="highest">🔴 Highest</option>
                            <option value="high">🟠 High</option>
                            <option value="medium">🟡 Medium</option>
                            <option value="low">🟢 Low</option>
                            <option value="lowest">⚪ Lowest</option>
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400 pointer-events-none" />
                        </div>
                      </div>

                      {/* Due Date Range */}
                      <div>
                        <label className="block text-xs font-semibold text-surface-400 uppercase tracking-wider mb-1.5">Due Date</label>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="date"
                            value={filters.dueDateFrom}
                            onChange={(e) => setFilters({ ...filters, dueDateFrom: e.target.value })}
                            className="flex-1 px-2 py-2 bg-surface-700 border border-surface-600 rounded-xl text-xs text-surface-200 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all"
                            title="From"
                          />
                          <span className="text-surface-500 text-xs">→</span>
                          <input
                            type="date"
                            value={filters.dueDateTo}
                            onChange={(e) => setFilters({ ...filters, dueDateTo: e.target.value })}
                            className="flex-1 px-2 py-2 bg-surface-700 border border-surface-600 rounded-xl text-xs text-surface-200 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all"
                            title="To"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Active Filter Pills */}
                    {activeFilterCount > 0 && (
                      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-surface-600">
                        <span className="text-xs text-surface-400 font-medium">Active:</span>
                        {filters.assignee !== 'all' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-brand-600/20 text-brand-300 rounded-lg text-xs font-medium">
                            {filters.assignee === 'unassigned' ? 'Unassigned' : projectMembers.find(u => u.id === filters.assignee)?.displayName || 'Assignee'}
                            <button onClick={() => handleAssigneeFilterChange('all')} className="hover:text-brand-100"><X className="w-3 h-3" /></button>
                          </span>
                        )}
                        {filters.reporter !== 'all' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-600/20 text-indigo-300 rounded-lg text-xs font-medium">
                            {filters.reporter === 'unknown' ? 'Unknown Reporter' : projectMembers.find(u => u.id === filters.reporter)?.displayName || 'Reporter'}
                            <button onClick={() => setFilters({ ...filters, reporter: 'all' })} className="hover:text-indigo-100"><X className="w-3 h-3" /></button>
                          </span>
                        )}
                        {filters.type !== 'all' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-600/20 text-green-300 rounded-lg text-xs font-medium">
                            {filters.type}
                            <button onClick={() => setFilters({ ...filters, type: 'all' })} className="hover:text-green-100"><X className="w-3 h-3" /></button>
                          </span>
                        )}
                        {filters.priority !== 'all' && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-600/20 text-orange-300 rounded-lg text-xs font-medium">
                            {filters.priority}
                            <button onClick={() => setFilters({ ...filters, priority: 'all' })} className="hover:text-orange-100"><X className="w-3 h-3" /></button>
                          </span>
                        )}
                        {(filters.dueDateFrom || filters.dueDateTo) && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-600/20 text-purple-300 rounded-lg text-xs font-medium">
                            Due: {filters.dueDateFrom || '...'} → {filters.dueDateTo || '...'}
                            <button onClick={() => setFilters({ ...filters, dueDateFrom: '', dueDateTo: '' })} className="hover:text-purple-100"><X className="w-3 h-3" /></button>
                          </span>
                        )}
                      </div>
                    )}

                    {/* Quick Assignee Avatars */}
                    {projectMembers.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-surface-600">
                        <span className="text-xs font-semibold text-surface-400 uppercase tracking-wider">Quick filter:</span>
                        <button
                          type="button"
                          onClick={() => handleAssigneeFilterChange('all')}
                          className={`flex items-center px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${isAssigneeFilterActive('all')
                            ? 'border-brand-500/50 bg-brand-600/20 text-brand-300 shadow-sm'
                            : 'border-surface-600 text-surface-400 hover:border-brand-500/40 hover:bg-brand-600/10'
                            }`}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAssigneeFilterChange('unassigned')}
                          className={`flex items-center px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${isAssigneeFilterActive('unassigned')
                            ? 'border-brand-500/50 bg-brand-600/20 text-brand-300 shadow-sm'
                            : 'border-surface-600 text-surface-400 hover:border-brand-500/40 hover:bg-brand-600/10'
                            }`}
                        >
                          Unassigned
                        </button>
                        {projectMembers.map((member) => {
                          const displayName = member.displayName || 'Member';
                          return (
                            <button
                              key={member.id}
                              type="button"
                              onClick={() => handleAssigneeFilterChange(member.id)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${isAssigneeFilterActive(member.id)
                                ? 'border-brand-500/50 bg-brand-600/20 text-brand-300 shadow-sm'
                                : 'border-surface-600 text-surface-400 hover:border-brand-500/40 hover:bg-brand-600/10'
                                }`}
                            >
                              <UserAvatar
                                name={displayName}
                                src={member.avatarUrl}
                                className="h-5 w-5 border border-surface-600"
                              />
                              <span>{displayName}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Main Content Area */}
          <div className="flex-1 overflow-auto bg-surface-900">
            {/* Error Display */}
            {apiError && (
              <div className="mx-6 mt-4 p-4 bg-red-900/30 border-l-4 border-red-500 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="text-red-400 text-xl">⚠️</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-red-300 text-sm font-medium">{apiError}</p>
                    <button
                      onClick={loadProjectIssues}
                      className="mt-2 text-red-400 hover:text-red-200 text-sm underline"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeToolsTab === 'board' ? (
              <>
                <div className="p-6 overflow-x-auto" style={{ minHeight: '600px' }}>
                  <div className="flex space-x-6 h-full min-w-max">
                    {columns.map((column) => {
                      const columnIssues = getIssuesByStatus(column.statuses);
                      const visibleCount = getVisibleCount(column.id);
                      const visibleIssues = columnIssues.slice(0, visibleCount);
                      const hasMore = columnIssues.length > visibleCount;
                      const remainingCount = columnIssues.length - visibleCount;

                      return (
                        <div key={column.id} className="flex-shrink-0 w-72">
                          <div className="bg-surface-800/60 rounded-xl border border-surface-700 h-full flex flex-col">
                            {/* Column Header */}
                            <div className={`px-3.5 py-3 border-b border-surface-700/60 bg-surface-800/80`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2.5">
                                  <div className={`w-2.5 h-2.5 rounded-full ${column.dotColor}`} />
                                  <h3 className="text-sm font-semibold text-surface-200">
                                    {column.title}
                                  </h3>
                                  <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 bg-surface-600 text-surface-300 text-2xs font-bold rounded-full">
                                    {column.count}
                                  </span>
                                </div>
                                <button
                                  onClick={() => onCreateIssue(project.id)}
                                  className="p-1 text-surface-400 hover:text-white hover:bg-surface-600 rounded-lg transition-colors"
                                >
                                  <AddIcon style={{ fontSize: 16 }} />
                                </button>
                              </div>
                            </div>

                            {/* Column Content */}
                            <div
                              className={`flex-1 p-3 space-y-2.5 overflow-y-auto min-h-[400px] max-h-[calc(100vh-320px)] transition-all duration-200 ${dragOverColumn === column.id
                                ? 'bg-brand-600/10 ring-2 ring-inset ring-dashed ring-brand-500/40'
                                : ''
                                }`}
                              onDragOver={handleDragOver}
                              onDragEnter={(e) => handleDragEnter(e, column.id)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, column.statuses[0])}
                              onScroll={(e) => {
                                // Load more issues when scrolled near the bottom
                                const target = e.currentTarget;
                                if (hasMore && target.scrollHeight - target.scrollTop - target.clientHeight < 100) {
                                  loadMoreForColumn(column.id);
                                }
                              }}
                            >
                              {visibleIssues.map((issue) => (
                                <div
                                  key={issue.id}
                                  draggable={!isLoadingIssues}
                                  onDragStart={(e) => handleDragStart(e, issue)}
                                  onDragEnd={handleDragEnd}
                                  className={`transition-all duration-200 ${isLoadingIssues || isUpdatingStatus
                                    ? 'cursor-not-allowed opacity-50'
                                    : 'cursor-move hover:shadow-lg hover:scale-105'
                                    } ${isUpdatingStatus === issue.id
                                      ? 'animate-pulse border-2 border-blue-300'
                                      : ''
                                    }`}
                                >
                                  <KanbanIssueCard
                                    issue={issue}
                                    users={users}
                                    onClick={() => handleIssueCardClick(issue)}
                                    onChatClick={() => handleOpenMessenger(issue)}
                                    onDelete={() => handleDeleteIssue(issue)}
                                    currentUserId={currentUser.id}
                                    isUpdating={isUpdatingStatus === issue.id}
                                  />
                                </div>
                              ))}

                              {/* Load More indicator */}
                              {hasMore && (
                                <button
                                  type="button"
                                  onClick={() => loadMoreForColumn(column.id)}
                                  className="w-full py-3 text-center text-sm font-medium text-brand-400 hover:text-brand-300 bg-brand-600/10 hover:bg-brand-600/20 rounded-lg border border-brand-500/30 transition-colors"
                                >
                                  Show more ({remainingCount} remaining)
                                </button>
                              )}

                              {columnIssues.length === 0 && (
                                <div className={`text-center py-12 transition-all duration-200 ${dragOverColumn === column.id
                                  ? 'bg-brand-600/15 border-2 border-dashed border-brand-500/40 rounded-lg'
                                  : ''
                                  }`}>
                                  {!isLoadingIssues && (
                                    <div className={`flex flex-col items-center space-y-3 ${dragOverColumn === column.id ? 'text-brand-300' : ''
                                      }`}>
                                      {dragOverColumn === column.id ? (
                                        <>
                                          <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center animate-bounce">
                                            <span className="text-brand-300 font-bold text-2xl">↓</span>
                                          </div>
                                          <div className="text-center">
                                            <p className="text-brand-300 font-medium">Drop here to move to {column.title}</p>
                                            <p className="text-sm text-brand-400 mt-1">
                                              Release to update issue status
                                            </p>
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div className="w-16 h-16 bg-surface-700 rounded-full flex items-center justify-center">
                                            <AddIcon className="text-surface-400" style={{ fontSize: 24 }} />
                                          </div>
                                          <div className="text-center">
                                            <p className="text-surface-300 font-medium">Create issue</p>
                                            <p className="text-sm text-surface-500 mt-1">
                                              Issues help you track ideas, feedback, tasks, or bugs for your work.
                                            </p>
                                          </div>
                                          <button
                                            onClick={() => onCreateIssue(project.id)}
                                            className="flex items-center space-x-2 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm"
                                          >
                                            <AddIcon style={{ fontSize: 16 }} />
                                            <span>Create New Issue</span>
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Drop zone indicator when dragging */}
                              {draggedIssue && dragOverColumn === column.id && columnIssues.length > 0 && (
                                <div className="border-2 border-dashed border-brand-500/40 bg-brand-600/10 rounded-lg p-4 text-center">
                                  <div className="text-brand-300 font-medium">Drop here to move to {column.title}</div>
                                  <div className="text-sm text-brand-400 mt-1">
                                    {draggedIssue.key} will be moved to {column.statuses[0]}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Loading State - Under the board */}
                {isLoadingIssues && (
                  <div className="mx-6 mt-4 p-8 text-center">
                    <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-surface-400">Loading project issues...</p>
                  </div>
                )}

              </>
            ) : (
              renderToolsContent()
            )}
          </div>
        </div>

        {/* Messenger */}
        {messengerIssue && (
          <IssueMessenger
            issue={messengerIssue}
            messages={messengerMessages}
            users={users}
            currentUser={currentUser}
            onSendMessage={handleSendMessage}
            isOpen={isMessengerOpen}
            onClose={() => {
              setIsMessengerOpen(false);
              setMessengerIssue(null);
            }}
          />
        )}
      </div>

      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add project member</h3>
            <p className="text-sm text-gray-600 mb-4">
              Enter an email address to add an existing user or send an invitation to join this project.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="name@example.com"
                />
              </div>
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    if (!inviteLoading) {
                      setInviteEmail('');
                      setShowAddMemberModal(false);
                    }
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                  disabled={inviteLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleInviteMember}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                  disabled={inviteLoading}
                >
                  {inviteLoading ? 'Sending…' : 'Send invite'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Create Issue Button

// Kanban-specific Issue Card Component
interface KanbanIssueCardProps {
  issue: Issue;
  users: User[];
  onClick: () => void;
  onChatClick: () => void;
  onDelete: () => void;
  currentUserId: string;
  isUpdating?: boolean;
}

const KanbanIssueCard: React.FC<KanbanIssueCardProps> = ({ issue, users, onClick, onChatClick, onDelete, currentUserId, isUpdating = false }) => {
  const assignee = users.find(u => u.id === issue.assignee);
  const mapAssignees = (assignees: any[] | undefined) =>
    (assignees ?? []).map(a => {
      const id = typeof a === "string" ? a : a.id;
      const displayName =
        typeof a === "string"
          ? a
          : a.display_name ?? a.displayName ?? a.name ?? "Unknown";
      const avatarUrl =
        typeof a === "string" ? "" : a.avatar_url ?? a.avatarUrl ?? "";
      return { id, displayName, avatarUrl };
    });
  const allAssignees = mapAssignees(issue.assignees_users);

  const reporter = users.find(u => u.id === issue.reporter);

  const getIssueTypeColor = (type: string) => {
    switch (type) {
      case 'bug':
        return 'bg-red-100 text-red-800';
      case 'story':
        return 'bg-green-100 text-green-800';
      case 'task':
        return 'bg-blue-100 text-blue-800';
      case 'epic':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'highest':
        return 'bg-red-100 text-red-700';
      case 'high':
        return 'bg-orange-100 text-orange-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-700';
      case 'lowest':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-surface-100 text-surface-600';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bug': return <BugIcon style={{ fontSize: 14 }} className="text-red-500" />;
      case 'story': return <StoryIcon style={{ fontSize: 14 }} className="text-green-500" />;
      case 'task': return <TaskIcon style={{ fontSize: 14 }} className="text-blue-500" />;
      case 'epic': return <EpicIcon style={{ fontSize: 14 }} className="text-purple-500" />;
      default: return <DefaultTypeIcon style={{ fontSize: 14 }} className="text-surface-400" />;
    }
  };

  const labelColors = [
    'bg-brand-100 text-brand-700',
    'bg-green-100 text-green-700',
    'bg-purple-100 text-purple-700',
    'bg-orange-100 text-orange-700',
  ];

  return (
    <div
      className={`bg-white rounded-lg border border-surface-200 p-3 cursor-pointer transition-all duration-200 group relative hover:shadow-elevated hover:border-brand-200 hover:-translate-y-0.5 ${isUpdating ? 'ring-2 ring-brand-300 shadow-elevated' : 'shadow-soft'}`}
    >
      {/* Loading overlay */}
      {isUpdating && (
        <div className="absolute inset-0 bg-white/80 rounded-lg flex items-center justify-center z-10">
          <div className="flex items-center gap-2 text-brand-600">
            <div className="w-3.5 h-3.5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-2xs font-medium">Updating...</span>
          </div>
        </div>
      )}

      {/* Labels */}
      {issue.labels.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {issue.labels.slice(0, 3).map((label, i) => (
            <span key={label} className={`text-2xs px-1.5 py-0.5 rounded font-medium ${labelColors[i % labelColors.length]}`}>
              {label}
            </span>
          ))}
          {issue.labels.length > 3 && (
            <span className="text-2xs text-surface-400">+{issue.labels.length - 3}</span>
          )}
        </div>
      )}

      {/* Title */}
      <div className="flex items-start gap-2" onClick={onClick}>
        <span className="mt-0.5 flex-shrink-0">{getTypeIcon(issue.type)}</span>
        <p className="text-xs font-bold text-black line-clamp-2 leading-snug flex-1 group-hover:text-brand-700 transition-colors">
          {issue.summary}
        </p>
      </div>

      {/* Meta Row */}
      <div className="flex items-center justify-between mt-2.5">
        <div className="flex items-center gap-2">
          <span className="text-2xs text-gray-700 font-semibold">{issue.key}</span>
          <span className={`text-2xs px-1.5 py-0.5 rounded font-semibold ${getPriorityColor(issue.priority)}`}>
            {issue.priority}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {issue.dueDate && (
            <span className="text-2xs text-gray-600 font-medium flex items-center gap-0.5">
              <AccessTimeIcon style={{ fontSize: 10 }} />
              {new Date(issue.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
          {issue.storyPoints && (
            <span className="text-2xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded font-semibold">
              {issue.storyPoints}sp
            </span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-2">
          {/* Reporter */}
          {reporter ? (
            <div className="flex items-center gap-1" title={`Reporter: ${reporter.displayName}`}>
              <UserAvatar
                name={reporter.displayName}
                src={reporter.avatarUrl}
                className="w-5 h-5 border border-gray-200"
              />
              <span className="text-2xs text-gray-700 font-medium truncate max-w-[60px]">{reporter.displayName.split(' ')[0]}</span>
            </div>
          ) : (
            <span className="text-2xs text-gray-500 font-medium">No reporter</span>
          )}

          {/* Action buttons */}
          <button
            onClick={(e) => { e.stopPropagation(); onChatClick(); }}
            className="p-0.5 text-gray-400 hover:text-brand-500 rounded transition-colors opacity-0 group-hover:opacity-100"
            title="Open chat"
          >
            <ChatIcon style={{ fontSize: 14 }} />
          </button>
          {issue.reporter === currentUserId && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-0.5 text-surface-300 hover:text-red-500 rounded transition-colors opacity-0 group-hover:opacity-100"
              title="Delete"
            >
              <DeleteIcon style={{ fontSize: 14 }} />
            </button>
          )}
        </div>

        {/* Assignees */}
        <div className="flex items-center">
          {allAssignees.length > 0 ? (
            <div className="flex -space-x-1.5">
              {allAssignees.slice(0, 3).map(user => (
                <UserAvatar
                  key={user.id}
                  name={user.displayName}
                  src={user.avatarUrl}
                  className="w-5 h-5 border-2 border-white shadow-sm"
                />
              ))}
              {allAssignees.length > 3 && (
                <div className="w-5 h-5 rounded-full bg-surface-200 border-2 border-white flex items-center justify-center text-2xs font-bold text-surface-600">
                  +{allAssignees.length - 3}
                </div>
              )}
            </div>
          ) : (
            <div className="w-5 h-5 rounded-full border-2 border-dashed border-surface-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <PersonAddIcon style={{ fontSize: 10 }} className="text-surface-300" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
